import React, { Component } from "react";
import ApiEndpoints from "../../../constants/api_endpoints";
import store from "../../../store";
import { setError, setLoader } from "../../../actions/action-creators";
import { downloadApiFile } from "../../../actions";
import { TablePagination } from "@material-ui/core";
import DatePicker from "react-datepicker";
import moment from "moment";
import SelectList from "../../global/form/select";

const DATE_FILTERS = ["All", "Curr Month", "Last Month", "Curr FY", "Last FY"];
const DATE_BASIS_OPTIONS = [
    { key: "CREATED_ON", label: "Created On" },
    { key: "REF_DATE",   label: "Ref Date" },
];

class UserWalletDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentWalletBalance: 0,
            balanceData: {},
            transactions: [],
            balanceByProjects: [],
            currentTransactionPage: 0,
            selectedDateFilter: 0,
            jobsMap: {},   // { job_id: job_title } — unfiltered, for entity lookup
            expandedTxnIdx: null,
            txnLogsCache: {},    // { transaction_id: [...logs] }
            loadingLogId: null,
            // transactions list + CSV download filters
            txnDateBasis: "CREATED_ON",
            txnStartDate: null,
            txnEndDate: null,
            txnCategories: [],          // empty = all transaction types
            categoryOptions: [],
        };
        this.txnRequestId = 0;
    }

    componentDidMount() {
        this.selectDateFilter(0);
        this.fetchJobsMap();
        this.fetchCategoryOptions();
    }

    fetchCategoryOptions = async () => {
        try {
            let resp = await ApiEndpoints.Userwallet.fetchWalletStatementCategories();
            this.setState({ categoryOptions: resp.data.data || [] });
        } catch (e) { /* silent — type filter stays empty, list shows all types */ }
    };

    fetchJobsMap = async () => {
        try {
            let resp = await ApiEndpoints.Userwallet.fetchUserBalanceDetails({ user_id: this.props.user_id });
            const jobsMap = {};
            (resp.data.data || []).forEach(j => { jobsMap[j.job_id] = j.job_title; });
            this.setState({ jobsMap });
        } catch (e) { /* silent — table falls back to raw entity_id */ }
    };

    selectDateFilter = async (dateFilterIndex) => {
        const filters = this.getDateFilters(dateFilterIndex);

        // transactions range follows the selected period; user can change it independently afterwards
        const txnFilters = {
            txnDateBasis: this.state.txnDateBasis,
            txnCategories: this.state.txnCategories,
            txnStartDate: filters.start_date || null,
            txnEndDate: filters.end_date || null,
        };
        this.setState({ selectedDateFilter: dateFilterIndex, ...txnFilters });

        store.dispatch(setLoader(true));
        await Promise.all([
            this.fetchBalance(filters),
            this.fetchBalanceByProjects(filters),
        ]);
        store.dispatch(setLoader(false));

        this.fetchTransactions(txnFilters);
    };

    // changes: any of { txnDateBasis, txnStartDate, txnEndDate, txnCategories }
    updateTxnFilters = (changes) => {
        const { txnDateBasis, txnStartDate, txnEndDate, txnCategories } = this.state;
        const txnFilters = { txnDateBasis, txnStartDate, txnEndDate, txnCategories, ...changes };
        this.setState(changes);
        this.fetchTransactions(txnFilters);
    };

    getTxnParams = ({ txnDateBasis, txnStartDate, txnEndDate, txnCategories }) => {
        const params = { user_id: this.props.user_id, date_basis: txnDateBasis };
        if (txnStartDate) params.start_date = moment(txnStartDate).startOf("day").toDate();
        if (txnEndDate)   params.end_date   = moment(txnEndDate).endOf("day").toDate();
        if (txnCategories && txnCategories.length) params.categories = txnCategories;
        return params;
    };

    downloadWalletLogs = async () => {
        const { txnDateBasis, txnStartDate, txnEndDate, txnCategories } = this.state;
        if (txnStartDate && txnEndDate && moment(txnStartDate).isAfter(txnEndDate, "day")) {
            return store.dispatch(setError({ show: true, message: "Start date cannot be after end date" }));
        }
        store.dispatch(setLoader(true));
        try {
            const resp = await ApiEndpoints.Userwallet.downloadWalletLogs(this.getTxnParams(this.state));
            const basisLabel = txnDateBasis === "REF_DATE" ? "Ref Date" : "Created On";
            const fmt = (d) => moment(d).format("DD-MMM-YYYY");
            const rangeLabel = txnStartDate || txnEndDate
                ? `${txnStartDate ? fmt(txnStartDate) : "Start"} to ${txnEndDate ? fmt(txnEndDate) : "Today"}`
                : "All";
            const typeLabel = txnCategories.length === 1 ? ` - ${txnCategories[0].replace(/\//g, "-")}`
                : txnCategories.length > 1 ? ` - ${txnCategories.length} Types` : "";
            downloadApiFile(resp, `Wallet Statement${typeLabel} - ${rangeLabel} - ${basisLabel}.csv`);
        } catch (e) {
            let message = e.toString();
            if (e.response && e.response.data) {
                // blob responseType: error body arrives as a Blob containing JSON
                try {
                    const body = e.response.data instanceof Blob ? JSON.parse(await e.response.data.text()) : e.response.data;
                    message = body.message || message;
                } catch (parseError) { /* keep generic message */ }
            }
            store.dispatch(setError({ show: true, message }));
        }
        store.dispatch(setLoader(false));
    };

    getDateFilters = (dateFilterIndex) => {
        let filters = {};
        if (dateFilterIndex === 1) {
            filters.start_date = moment().startOf("month").toDate();
            filters.end_date   = moment().endOf("month").toDate();
        }
        if (dateFilterIndex === 2) {
            filters.start_date = moment().subtract(1, "month").startOf("month").toDate();
            filters.end_date   = moment().subtract(1, "month").endOf("month").toDate();
        }
        if (dateFilterIndex === 3) {
            const today = moment();
            const fyStart = today.month() >= 3 ? moment([today.year(), 3, 1]) : moment([today.year() - 1, 3, 1]);
            filters.start_date = fyStart.startOf("day").toDate();
            filters.end_date   = fyStart.clone().add(1, "year").subtract(1, "day").endOf("day").toDate();
        }
        if (dateFilterIndex === 4) {
            const today = moment();
            const fyStart = today.month() >= 3 ? moment([today.year() - 1, 3, 1]) : moment([today.year() - 2, 3, 1]);
            filters.start_date = fyStart.startOf("day").toDate();
            filters.end_date   = fyStart.clone().add(1, "year").subtract(1, "day").endOf("day").toDate();
        }
        return filters;
    };

    fetchBalance = async (filters = {}) => {
        try {
            let resp = await ApiEndpoints.Userwallet.fetchUserBalance({ user_id: this.props.user_id, ...filters });
            let balanceData = resp.data.data[0] || {};
            let currentWalletBalance = Object.keys(filters).length === 0 ? balanceData.total_amount : this.state.currentWalletBalance;
            this.setState({ balanceData, currentWalletBalance });
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
    };

    fetchBalanceByProjects = async (filters = {}) => {
        try {
            let resp = await ApiEndpoints.Userwallet.fetchUserBalanceDetails({ user_id: this.props.user_id, ...filters });
            this.setState({ balanceByProjects: resp.data.data });
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
    };

    fetchTransactions = async (txnFilters) => {
        // ignore responses of older requests when filters change quickly
        const requestId = ++this.txnRequestId;
        this.appliedCategoriesKey = (txnFilters.txnCategories || []).join("|");
        try {
            let resp = await ApiEndpoints.Userwallet.fetchWalletStatement(this.getTxnParams(txnFilters));
            if (requestId !== this.txnRequestId) return;
            this.setState({ transactions: resp.data.data || [], currentTransactionPage: 0, expandedTxnIdx: null });
        } catch (e) {
            if (requestId !== this.txnRequestId) return;
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
        }
    };

    fetchTxnLogs = async (txn, pageIdx) => {
        const tid = txn.transaction_id;
        const { txnLogsCache, expandedTxnIdx } = this.state;

        // collapse if already open
        if (expandedTxnIdx === pageIdx) {
            this.setState({ expandedTxnIdx: null });
            return;
        }

        // use cached result if available
        if (txnLogsCache[tid]) {
            this.setState({ expandedTxnIdx: pageIdx });
            return;
        }

        this.setState({ loadingLogId: tid, expandedTxnIdx: pageIdx });
        try {
            let resp = await ApiEndpoints.Userwallet.fetchTransactionLogs({ transaction_id: tid, user_id: this.props.user_id });
            const logs = resp.data.data || [];
            this.setState(prev => ({ txnLogsCache: { ...prev.txnLogsCache, [tid]: logs }, loadingLogId: null }));
        } catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
            this.setState({ expandedTxnIdx: null, loadingLogId: null });
        }
    };

    renderDateFilter() {
        let { selectedDateFilter } = this.state;
        return (
            <div style={S.filterRow}>
                {DATE_FILTERS.map((label, i) => (
                    <button
                        key={i}
                        style={{ ...S.filterPill, ...(selectedDateFilter === i ? S.filterPillActive : {}) }}
                        onClick={() => this.selectDateFilter(i)}
                    >
                        {label}
                    </button>
                ))}
            </div>
        );
    }

    // multi-select stays open while ticking; refetch once on close, only if the selection changed
    applyCategoryFilter = () => {
        if (this.state.txnCategories.join("|") !== this.appliedCategoriesKey) this.updateTxnFilters({});
    };

    renderTransactionsHeader() {
        const { txnDateBasis, txnStartDate, txnEndDate, txnCategories, categoryOptions } = this.state;
        return (
            <div style={S.txnHeader}>
                <div style={{ ...S.sectionTitle, marginBottom: 0 }}>Transactions</div>
                <div style={S.downloadControls}>
                    <div style={S.segmented} title="Date used to filter transactions and the download">
                        {DATE_BASIS_OPTIONS.map(opt => (
                            <button
                                key={opt.key}
                                style={{ ...S.segment, ...(txnDateBasis === opt.key ? S.segmentActive : {}) }}
                                onClick={() => txnDateBasis !== opt.key && this.updateTxnFilters({ txnDateBasis: opt.key })}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <div style={S.datePickerWrap}>
                        <DatePicker
                            selected={txnStartDate}
                            onChange={date => this.updateTxnFilters({ txnStartDate: date })}
                            maxDate={txnEndDate || undefined}
                            isClearable={true}
                            autoComplete="off"
                            dateFormat="dd-MMM-yyyy"
                            placeholderText="Start Date"
                            className="form-control form-control-sm"
                        />
                    </div>
                    <span style={{ color: "#aaa", fontSize: 12 }}>to</span>
                    <div style={S.datePickerWrap}>
                        <DatePicker
                            selected={txnEndDate}
                            onChange={date => this.updateTxnFilters({ txnEndDate: date })}
                            minDate={txnStartDate || undefined}
                            isClearable={true}
                            autoComplete="off"
                            dateFormat="dd-MMM-yyyy"
                            placeholderText="End Date"
                            className="form-control form-control-sm"
                        />
                    </div>
                    <div style={S.typeSelectWrap} title="Transaction types (none selected = all)">
                        <SelectList
                            multiple="checkbox"
                            placeholder="All Types"
                            value={txnCategories}
                            onChange={e => this.setState({ txnCategories: (e.target.value || []).filter(Boolean) })}
                            onClose={this.applyCategoryFilter}
                            style={{ fontSize: 13 }}
                        >
                            {categoryOptions.map(category => <option key={category} value={category}>{category}</option>)}
                        </SelectList>
                    </div>
                    <button style={S.downloadButton} onClick={this.downloadWalletLogs}>
                        ⬇ Download CSV
                    </button>
                </div>
            </div>
        );
    }

    renderSummary() {
        let { balanceData, currentWalletBalance } = this.state;
        const kpis = [
            { label: "All Earnings",  value: balanceData.earning_amount          || 0, accent: "#27ae60" },
            { label: "Referral",      value: balanceData.referral_amount         || 0, accent: "#1677ff" },
            { label: "TDS Deducted",  value: balanceData.tds_deduction_amount    || 0, accent: "#e8a020" },
            ...(balanceData.security_amount ? [{ label: "Deposit", value: balanceData.security_amount, accent: "#888" }] : []),
        ];
        return (
            <div>
                <div style={S.balanceCard}>
                    <span style={S.balanceLabel}>Current Wallet Balance</span>
                    <span style={S.balanceValue}>₹{Number(currentWalletBalance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                </div>
                {this.renderDateFilter()}
                <div style={S.kpiRow}>
                    {kpis.map((k, i) => (
                        <div key={i} style={{ ...S.kpiCard, borderLeftColor: k.accent }}>
                            <span style={S.kpiLabel}>{k.label}</span>
                            <div style={S.kpiValue}>₹{Number(k.value).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                        </div>
                    ))}
                </div>
                {this.renderTransactionsHeader()}
                {this.renderTransactionTable()}
            </div>
        );
    }

    renderProjects() {
        let { balanceByProjects } = this.state;
        return (
            <div>
                {this.renderDateFilter()}
                <div style={S.sectionTitle}>Project-wise Earnings</div>
                {balanceByProjects.length === 0 ? (
                    <div style={S.emptyState}>No project earnings found for the selected period.</div>
                ) : (
                    <div style={S.tableWrapper}>
                        <table style={S.table}>
                            <thead>
                                <tr style={S.tHead}>
                                    <th style={S.th}>Project</th>
                                    <th style={{ ...S.th, textAlign: "center" }}>Verified Tasks</th>
                                    <th style={{ ...S.th, textAlign: "right" }}>Total Earning</th>
                                </tr>
                            </thead>
                            <tbody>
                                {balanceByProjects.map((job, i) => (
                                    <tr key={i} style={S.tRow}>
                                        <td style={S.td}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                <img alt="logo" src={job.logo_url} style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover", background: "#f0f0f0" }} />
                                                <span style={{ fontWeight: 600, color: "#1a1a1a" }}>{job.job_title}</span>
                                            </div>
                                        </td>
                                        <td style={{ ...S.td, textAlign: "center", fontWeight: 700, color: "#1677ff" }}>
                                            {job.verified_count || 0}
                                        </td>
                                        <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: "#1a7a42" }}>
                                            ₹{Number(job.total_credit || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    renderLogPanel(txn, jobsMap) {
        const { txnLogsCache, loadingLogId } = this.state;
        const tid = txn.transaction_id;
        const logs = txnLogsCache[tid];

        if (loadingLogId === tid) {
            return <div style={{ padding: "14px 0", color: "#888", fontSize: 12 }}>Loading logs…</div>;
        }
        if (!logs || logs.length === 0) {
            return <div style={{ padding: "14px 0", color: "#aaa", fontSize: 12 }}>No log entries found.</div>;
        }

        const fmtPayType = (s) => s ? s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "—";
        const fmtRefDate = (v) => { const m = moment(v); return (v && m.isValid()) ? m.utcOffset("+05:30").format("Do MMM'YY") : "—"; };

        return (
            <div style={S.logPanel}>
                <div style={S.logPanelTitle}>Transaction Logs ({logs.length})</div>
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: "#f9f9f9" }}>
                                <th style={S.logTh}>#</th>
                                <th style={S.logTh}>Amount</th>
                                <th style={S.logTh}>Source</th>
                                <th style={S.logTh}>Reference</th>
                                <th style={S.logTh}>Ref. Date</th>
                                <th style={S.logTh}>Payment Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log, li) => {
                                const isLead = log.subentity_type === "FORM_RESPONSE";
                                return (
                                    <tr key={li} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                        <td style={S.logTd}>{li + 1}</td>
                                        <td style={{ ...S.logTd, fontWeight: 700, color: "#1a7a42" }}>
                                            ₹{Number(log.log_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                        </td>
                                        <td style={S.logTd}>
                                            <span style={S.logTypeBadge}>{log.entity_type || "—"}</span>
                                            <span style={{ marginLeft: 5, color: "#333" }}>
                                                {log.entity_type === "JOB"
                                                    ? (jobsMap[log.entity_id] || log.entity_id || "—")
                                                    : (log.entity_id || "—")}
                                            </span>
                                        </td>
                                        <td style={S.logTd}>
                                            <span style={{ ...S.logTypeBadge, background: isLead ? "#fff7e6" : "#f0f0f0", color: isLead ? "#d46b08" : "#555" }}>
                                                {isLead ? "Lead" : (log.subentity_type || "—")}
                                            </span>
                                            <span style={{ marginLeft: 5, color: "#333", fontFamily: "monospace" }}>
                                                {log.subentity_id != null ? (isLead ? `REF${log.subentity_id}` : log.subentity_id) : "—"}
                                            </span>
                                        </td>
                                        <td style={{ ...S.logTd, whiteSpace: "nowrap", color: "#555" }}>
                                            {fmtRefDate(log.ref_date)}
                                        </td>
                                        <td style={S.logTd}>
                                            <span style={{ fontSize: 11, color: "#555" }}>{fmtPayType(log.payment_type)}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderTransactionTable() {
        let { transactions, currentTransactionPage, jobsMap, expandedTxnIdx } = this.state;
        const page = transactions.slice(currentTransactionPage * 10, currentTransactionPage * 10 + 10);
        if (transactions.length === 0) {
            return <div style={S.emptyState}>No transactions found for the selected period.</div>;
        }
        return (
            <>
                <div style={S.tableWrapper}>
                    <table style={S.table}>
                        <thead>
                            <tr style={S.tHead}>
                                <th style={S.th}>Amount</th>
                                <th style={S.th}>Description</th>
                                <th style={S.th}>Source</th>
                                <th style={{ ...S.th, textAlign: "right" }}>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {page.map((txn, i) => {
                                const isExpanded = expandedTxnIdx === i;
                                const logCount = txn.logs ? txn.logs.length : 0;
                                return (
                                    <React.Fragment key={i}>
                                        <tr style={{ ...S.tRow, background: isExpanded ? "#fafafa" : "transparent" }}>
                                            <td style={S.td}>
                                                <span style={{ fontWeight: 700, fontSize: 14, color: txn.type === 1 ? "#1a7a42" : "#c0392b" }}>
                                                    {txn.type === 1 ? "+" : "−"}₹{Number(txn.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                </span>
                                                {/* <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: "#fff", background: txn.type === 1 ? "#1a7a42" : "#c0392b", borderRadius: 999, padding: "1px 7px" }}>
                                                    {txn.type === 1 ? "Credit" : "Debit"}
                                                </span> */}
                                            </td>
                                            <td style={S.td}>
                                                <div style={{ fontSize: 13, color: "#1a1a1a" }}>{txn.reason}</div>
                                                {txn.remark && <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{txn.remark}</div>}
                                            </td>
                                            <td style={S.td}>
                                                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                                                    <div style={{ flex: 1 }}>
                                                        {txn.logs && txn.logs.length ? txn.logs.map((log, li) => (
                                                            <div key={li} style={{ marginBottom: li < txn.logs.length - 1 ? 6 : 0 }}>
                                                                <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 1 }}>
                                                                    {log.entity_type || "—"}
                                                                </div>
                                                                <div style={{ fontSize: 12, color: "#333", fontWeight: 500 }}>
                                                                    {log.project
                                                                        || (log.entity_type === "JOB" && jobsMap[log.entity_id])
                                                                        || log.entity_id
                                                                        || "—"}
                                                                </div>
                                                            </div>
                                                        )) : "—"}
                                                    </div>
                                                    <span
                                                        title="View full transaction logs"
                                                        style={{ ...S.infoIcon, background: isExpanded ? "#1677ff" : "#edf2ff", color: isExpanded ? "#fff" : "#1677ff" }}
                                                        onClick={(e) => { e.stopPropagation(); this.fetchTxnLogs(txn, i); }}
                                                    >
                                                        i
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>
                                                {moment(txn.created_on).utcOffset("+05:30").format("Do MMM'YY HH:mm")}
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr style={{ background: "#fafafa" }}>
                                                <td colSpan={4} style={{ padding: "0 14px 14px 14px", borderBottom: "1px solid #e8e8e8" }}>
                                                    {this.renderLogPanel(txn, jobsMap)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={transactions.length}
                    rowsPerPage={10}
                    page={currentTransactionPage}
                    onPageChange={(_, p) => this.setState({ currentTransactionPage: p, expandedTxnIdx: null })}
                />
            </>
        );
    }

    render() {
        const { view } = this.props;

        // view prop: 'summary' | 'projects' | 'transactions' | undefined (show all)
        if (view === "projects")     return this.renderProjects();
        if (view === "transactions") return this.renderTransactions();

        // 'summary' or no view prop — show full summary (legacy compat)
        return (
            <div>
                {this.renderSummary()}
                {!view && (
                    <>
                        <div style={{ ...S.sectionTitle, marginTop: 24 }}>Project Earnings</div>
                        {this.state.balanceByProjects.map((job, i) => (
                            <div key={i} style={S.legacyProjectRow}>
                                <img alt="logo" src={job.logo_url} style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover" }} />
                                <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{job.job_title}</span>
                                <span style={{ fontSize: 13, color: "#1a7a42", fontWeight: 700 }}>
                                    ₹{Number(job.total_credit || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                        ))}

                        <div style={{ ...S.sectionTitle, marginTop: 24 }}>Transactions</div>
                        <table style={{ ...S.table, marginBottom: 4 }}>
                            <thead>
                                <tr style={S.tHead}>
                                    <th style={S.th}>Amount</th>
                                    <th style={S.th}>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.transactions
                                    .slice(this.state.currentTransactionPage * 10, this.state.currentTransactionPage * 10 + 10)
                                    .map((txn, i) => (
                                        <tr key={i} style={S.tRow}>
                                            <td style={{ ...S.td, color: txn.type === 1 ? "#1a7a42" : "#c0392b", fontWeight: 600 }}>
                                                {txn.type === 1 ? "+" : "−"}{txn.amount}
                                            </td>
                                            <td style={S.td}>
                                                <div>{txn.reason}</div>
                                                <div style={{ fontSize: 11, color: "#aaa" }}>
                                                    {txn.remark} | {moment(txn.created_on).utcOffset("+05:30").format("Do MMM'YY HH:mm")}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                        <TablePagination
                            rowsPerPageOptions={[10]}
                            component="div"
                            count={this.state.transactions.length}
                            rowsPerPage={10}
                            page={this.state.currentTransactionPage}
                            onPageChange={(_, p) => this.setState({ currentTransactionPage: p })}
                        />
                    </>
                )}
            </div>
        );
    }
}

const S = {
    /* date filter */
    filterRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 },
    filterPill: {
        padding: "6px 16px",
        borderRadius: 999,
        border: "1.5px solid #e0e0e0",
        background: "#fff",
        fontSize: 13,
        fontWeight: 500,
        color: "#555",
        cursor: "pointer",
        transition: "all 0.15s",
        outline: "none",
    },
    filterPillActive: {
        background: "#c0392b",
        borderColor: "#c0392b",
        color: "#fff",
    },
    /* transactions header + wallet logs download */
    txnHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 10,
        marginTop: 24,
        marginBottom: 12,
    },
    downloadControls: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    datePickerWrap: { width: 130 },
    typeSelectWrap: { width: 160 },
    segmented: { display: "flex", border: "1px solid #e0e0e0", borderRadius: 999, overflow: "hidden" },
    segment: {
        padding: "2px 8px",
        border: "none",
        background: "#fff",
        fontSize: 11,
        lineHeight: "16px",
        fontWeight: 500,
        color: "#555",
        cursor: "pointer",
        outline: "none",
    },
    segmentActive: { background: "#1677ff", color: "#fff" },
    downloadButton: {
        padding: "6px 16px",
        borderRadius: 999,
        border: "1.5px solid #1677ff",
        background: "#fff",
        color: "#1677ff",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        outline: "none",
    },
    /* balance card */
    balanceCard: {
        background: "#f0f7ff",
        border: "1px solid #bfd8f7",
        borderRadius: 10,
        padding: "14px 20px",
        marginBottom: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    balanceLabel: { fontSize: 14, fontWeight: 600, color: "#444" },
    balanceValue: { fontSize: 18, fontWeight: 800, color: "#1a1a1a" },
    /* KPI row */
    kpiRow: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: 12,
        marginBottom: 4,
    },
    kpiCard: {
        border: "1px solid #e8e8e8",
        borderLeft: "3px solid #e0e0e0",
        borderRadius: 10,
        padding: "12px 16px",
        background: "#fff",
    },
    kpiLabel: {
        display: "block",
        fontSize: 11,
        color: "#999",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        marginBottom: 5,
    },
    kpiValue: { fontSize: 18, fontWeight: 800, color: "#1a1a1a" },
    /* section title */
    sectionTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: "#1a1a1a",
        marginBottom: 12,
    },
    /* table */
    tableWrapper: {
        border: "1px solid #e8e8e8",
        borderRadius: 10,
        overflow: "hidden",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    tHead: { background: "#f5f5f5" },
    th: {
        padding: "10px 14px",
        textAlign: "left",
        fontWeight: 600,
        color: "#555",
        borderBottom: "1px solid #e8e8e8",
        whiteSpace: "nowrap",
    },
    tRow: { borderBottom: "1px solid #f0f0f0" },
    td: { padding: "11px 14px", color: "#333", verticalAlign: "middle" },
    /* legacy project row */
    legacyProjectRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        border: "1px solid #f0f0f0",
        borderRadius: 10,
        background: "#fff",
        marginBottom: 8,
    },
    /* empty */
    emptyState: {
        padding: "40px 20px",
        textAlign: "center",
        color: "#aaa",
        fontSize: 14,
    },
    /* small "i" icon in Source cell */
    infoIcon: {
        flexShrink: 0,
        width: 18,
        height: 18,
        borderRadius: "50%",
        fontSize: 11,
        fontWeight: 800,
        fontStyle: "italic",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        userSelect: "none",
        lineHeight: 1,
    },
    /* expanded log panel */
    logPanel: {
        border: "1px solid #e8e8e8",
        borderRadius: 10,
        overflow: "hidden",
        marginTop: 10,
        background: "#fff",
    },
    logPanelTitle: {
        fontSize: 11,
        fontWeight: 700,
        color: "#888",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        padding: "8px 14px",
        borderBottom: "1px solid #f0f0f0",
        background: "#f9f9f9",
    },
    logTh: {
        padding: "7px 12px",
        textAlign: "left",
        fontWeight: 600,
        color: "#777",
        borderBottom: "1px solid #f0f0f0",
        whiteSpace: "nowrap",
    },
    logTd: { padding: "8px 12px", color: "#333", verticalAlign: "middle" },
    logTypeBadge: {
        background: "#f0f0f0",
        color: "#555",
        borderRadius: 6,
        padding: "2px 8px",
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
    },
};

export default UserWalletDetails;
